import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Cookies from "universal-cookie";
import { apiUrl } from "../commons/inFormTypes";
import AuthLayout from "../components/User/AuthLayout";
import GoogleLoginButton from "../components/User/GoogleLoginButton";
import { useLanguage } from "../i18n/LanguageContext";

const Login = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const requestedRedirect = searchParams.get("redirect");
    const redirectTo = requestedRedirect?.startsWith("/") && !requestedRedirect.startsWith("//") ? requestedRedirect : null;
    const { isEnglish } = useLanguage();
    const t = isEnglish ? { error: "The email or password is incorrect. Please try again.", noAccount: "Do not have an account?", signUp: "Create one", title: "Sign in to", welcome: "Welcome back. Enter your account details to continue.", email: "Email address", password: "Password", hide: "Hide password", show: "Show password", remember: "Remember me", forgot: "Forgot your password?", loading: "Signing in...", submit: "Sign in", or: "or" } : { error: "ایمیل یا رمز عبور صحیح نیست. لطفاً دوباره تلاش کنید.", noAccount: "حساب کاربری ندارید؟", signUp: "ثبت‌نام کنید", title: "ورود به", welcome: "خوش آمدید؛ برای ورود به سامانه اطلاعات حساب خود را وارد کنید.", email: "آدرس ایمیل", password: "رمز عبور", hide: "پنهان کردن رمز عبور", show: "نمایش رمز عبور", remember: "مرا به خاطر بسپار", forgot: "رمز عبور را فراموش کرده‌اید؟", loading: "در حال ورود...", submit: "ورود", or: "یا" };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError("");
        setIsLoading(true);

        try {
            const response = await axios.post(`${apiUrl}auth/Login`, {
                Email: email.trim(),
                Password: password,
            });

            if (!response.data?.token) throw new Error("INVALID_CREDENTIALS");

            const cookies = new Cookies();
            cookies.set("token", response.data.token, { path: "/", sameSite: "lax" });
            cookies.set("role", response.data.role, { path: "/", sameSite: "lax" });

            if (String(response.data.role).toLowerCase() === "admin") navigate(redirectTo || "/mainAdmin/users");
            else navigate(redirectTo || "/user/dashboard");
        } catch {
            setError(t.error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthLayout mode="signIn">
            <p className="auth-switch">
                {t.noAccount}
                <Link to={`/SignUp?redirect=${encodeURIComponent(redirectTo || "/user/dashboard")}`}>{t.signUp}</Link>
            </p>

            <header className="auth-heading">
                <h1>{t.title} <span>Infinity Apply</span></h1>
                <p>{t.welcome}</p>
            </header>

            <form className="auth-form" onSubmit={handleSubmit}>
                <div className="auth-field">
                    <label htmlFor="login-email">{t.email}</label>
                    <div className="auth-input-wrap">
                        <input id="login-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="your.email@example.com" autoComplete="email" dir="ltr" required />
                    </div>
                </div>

                <div className="auth-field">
                    <label htmlFor="login-password">{t.password}</label>
                    <div className="auth-input-wrap auth-input-wrap--password">
                        <input id="login-password" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="••••••••" autoComplete="current-password" dir="ltr" required />
                        <button className="auth-password-toggle" type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? t.hide : t.show}>
                            {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                        </button>
                    </div>
                </div>

                <div className="auth-options">
                    <label className="auth-remember"><input type="checkbox" /> {t.remember}</label>
                    <a className="auth-forgot" href="mailto:support@infinityapply.com?subject=Password recovery">{t.forgot}</a>
                </div>

                {error && <p className="auth-error" role="alert">{error}</p>}

                <button className="auth-submit" type="submit" disabled={isLoading || !email || !password}>
                    {isLoading && <span className="auth-spinner" aria-hidden="true" />}
                    {isLoading ? t.loading : t.submit}
                </button>

                <div className="auth-divider">{t.or}</div>
                <div className="auth-google"><GoogleLoginButton redirectTo={redirectTo} /></div>
            </form>
        </AuthLayout>
    );
};

export default Login;
